import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Standing } from "../../../models/Standing";
import { Match } from "../../../models/Match";
import { PlayerStats } from "../../../models/PlayerStats";
import { RosterTransfer } from "../../../models/RosterTransfer";
import { getPlayersByTeamIdInDivision } from "../../../services/PlayerService";
import { getMatchesForTeamBySeason } from "../../../services/MatchService";
import { fetchTeamTransfers } from "../../../services/TransferService";
import PlayerImage from "../../../components/PlayerImage";
import { parseMatchTime } from "../../../utils/matchTime";
import { Column, colorVars, formatAvg } from "./contentTeamShared";
import DeviationChip from "./DeviationChip";
import styles from "./ContentTeam.module.css";

interface Props {
  season: number;
  division: string;
  divisionName: string;
  standing: Standing;
  zoneLabel: string | null;
  columns: Column[];
  played: boolean;
}

interface TeamData {
  key: string;
  players: PlayerStats[] | null;
  matches: Match[] | null;
  transfers: RosterTransfer[];
}

const ExternalIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="8 7 17 7 17 16" />
  </svg>
);

/**
 * The selected team: its place, each predictor's guess, and its roster and matches in the division
 * being viewed (a team's kval maps, or its other division's, don't belong to this table).
 */
const TeamPanel: React.FC<Props> = ({ season, division, divisionName, standing, zoneLabel, columns, played }) => {
  const { t, i18n } = useTranslation("pages");
  const locale = i18n.language === "en" ? "en-GB" : "da-DK";
  const teamId = standing.team.teamId;
  // A team can sit in two tables (a ligaen team's 1div squad), so the division is part of the key.
  const key = `${season}|${division}|${teamId}`;
  const [tab, setTab] = useState<"players" | "matches">("players");
  const [data, setData] = useState<TeamData | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      getPlayersByTeamIdInDivision(teamId, season, division),
      getMatchesForTeamBySeason(teamId, season),
      fetchTeamTransfers(teamId, season),
    ]).then(([players, matches, transfers]) => {
      if (cancelled) return;
      setData({
        key: `${season}|${division}|${teamId}`,
        players: players.status === "fulfilled" ? players.value ?? [] : null,
        matches: matches.status === "fulfilled" ? (matches.value ?? []).filter((m) => m.division === division) : null,
        transfers: transfers.status === "fulfilled" ? transfers.value ?? [] : [],
      });
    });
    return () => {
      cancelled = true;
    };
  }, [teamId, season, division]);

  const current = data?.key === key ? data : null;
  const day = (value: string | Date) => new Date(value).toLocaleDateString(locale, { day: "numeric", month: "short" });

  const players = useMemo(() => {
    if (!current?.players) return null;
    const joins = new Map<string, string>();
    const leaves = new Map<string, string>();
    for (const tr of current.transfers) {
      if (tr.toTeamId === teamId && (!joins.has(tr.playerSteamId) || tr.transferDate < joins.get(tr.playerSteamId)!)) joins.set(tr.playerSteamId, tr.transferDate);
      if (tr.fromTeamId === teamId && (!leaves.has(tr.playerSteamId) || tr.transferDate > leaves.get(tr.playerSteamId)!)) leaves.set(tr.playerSteamId, tr.transferDate);
    }
    // Players from the first roster snapshot have no join of their own: they joined with the team.
    const firstJoin = Array.from(joins.values()).sort()[0];
    return current.players
      .map((p) => ({ p, since: joins.get(p.steamID) ?? firstJoin, until: leaves.get(p.steamID) }))
      .sort((a, b) => Number(!!a.until) - Number(!!b.until) || b.p.gamesPlayed - a.p.gamesPlayed || (b.p.rating30 ?? 0) - (a.p.rating30 ?? 0));
  }, [current, teamId]);

  const avgRating = useMemo(() => {
    const rated = (players ?? []).filter((x) => !x.until && (x.p.rating30 ?? 0) > 0);
    return rated.length ? rated.reduce((sum, x) => sum + (x.p.rating30 ?? 0), 0) / rated.length : null;
  }, [players]);

  const matches = useMemo(
    () => (current?.matches ? [...current.matches].sort((a, b) => parseMatchTime(b.match_time).getTime() - parseMatchTime(a.match_time).getTime()) : null),
    [current]
  );

  return (
    <aside className={styles.aside} aria-label={standing.team.teamName}>
      <section className={styles.panel}>
        <div className={styles.teamHead}>
          <img className={styles.teamHeadLogo} src={standing.team.teamLogo} alt="" />
          <div className={styles.teamHeadText}>
            <span className={styles.muted}>{zoneLabel ? `${divisionName} · ${zoneLabel}` : divisionName}</span>
            <h2 className={styles.teamHeadName}>{standing.team.teamName}</h2>
            <Link className={styles.ext} to={`/team/${teamId}?season=${season}`} target="_blank" rel="noopener noreferrer">
              {t("contentTeam.teamPage")} <ExternalIcon />
            </Link>
          </div>
        </div>
        <div className={styles.teamStats}>
          <div className={styles.teamStat}>
            <span className={styles.statValue}>{standing.rank}.</span>
            <span className={styles.statLabel}>{t("contentTeam.placement")}</span>
          </div>
          <div className={styles.teamStat}>
            <span className={styles.statValue}>{standing.points}</span>
            <span className={styles.statLabel}>{t("contentTeam.points")}</span>
          </div>
          <div className={styles.teamStat}>
            <span className={styles.statValue}>{standing.wins}-{standing.gamesPlayed - standing.wins}</span>
            <span className={styles.statLabel}>{t("contentTeam.matches")}</span>
          </div>
        </div>
        <div className={styles.guesses}>
          <span className={styles.sectionLabel}>{t("contentTeam.guesses")}</span>
          {columns.map((c) => {
            const predicted = c.ranks?.get(teamId);
            return (
              <div key={c.key} className={styles.guessRow} style={colorVars(c.color)}>
                <span className={styles.guessWho}>
                  <span className={styles.guessWhoName}>{c.name}</span>
                </span>
                {predicted === undefined ? (
                  <span className={styles.none}>{t("contentTeam.noGuess")}</span>
                ) : (
                  <span className={styles.guessWhat}>
                    <span className={styles.muted}>{t("contentTeam.guessed")}</span>
                    <span className={styles.guessRank}>{predicted}.</span>
                    {played && <DeviationChip predicted={predicted} actual={standing.rank} />}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.tabs} role="tablist" aria-label={t("contentTeam.details")}>
          <button type="button" role="tab" aria-selected={tab === "players"} className={`${styles.tab} ${tab === "players" ? styles.tabOn : ""}`} onClick={() => setTab("players")}>
            {t("contentTeam.playersTab")}
            {players && <span className={styles.tabCount}>{players.length}</span>}
          </button>
          <button type="button" role="tab" aria-selected={tab === "matches"} className={`${styles.tab} ${tab === "matches" ? styles.tabOn : ""}`} onClick={() => setTab("matches")}>
            {t("contentTeam.matchesTab")}
            {matches && <span className={styles.tabCount}>{matches.length}</span>}
          </button>
        </div>

        {!current && <div className={styles.listEmpty}>…</div>}

        {current && tab === "players" && (
          <>
            <div className={styles.tabIntro}>
              <span>{t("contentTeam.divisionOnly", { division: divisionName })}</span>
              <span>
                {t("contentTeam.avgRating")} <strong>{formatAvg(avgRating)}</strong>
              </span>
            </div>
            <div className={styles.list}>
              {players === null && <div className={styles.listEmpty}>{t("contentTeam.detailsFailed")}</div>}
              {players?.length === 0 && <div className={styles.listEmpty}>{t("contentTeam.noPlayers")}</div>}
              {players?.map(({ p, since, until }) => (
                <Link key={p.steamID} to={`/player/${p.steamID}`} className={`${styles.listRow} ${styles.playerRow} ${until ? styles.playerLeft : ""}`}>
                  <PlayerImage id={p.steamID} width={72} imgStyle={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", objectPosition: "top", background: "#2e2e2e" }} />
                  <span className={styles.playerMain}>
                    <span className={styles.playerName}>
                      <span className={styles.playerNameText}>{p.account?.playerName ?? p.steamID}</span>
                      {p.account?.role && <span className={styles.role}>{p.account.role}</span>}
                    </span>
                    <span className={styles.playerSub}>
                      {t("contentTeam.games", { count: p.gamesPlayed })}
                      {since && (
                        <>
                          {" · "}
                          <span className={until ? styles.stintGone : styles.stintNow}>
                            {day(since)} → {until ? day(until) : t("contentTeam.now")}
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                  <span className={styles.rating}>
                    <span className={styles.ratingValue}>{formatAvg(p.rating30)}</span>
                    <span className={styles.ratingLabel}>Rating 3.0</span>
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}

        {current && tab === "matches" && (
          <div className={styles.list}>
            {matches === null && <div className={styles.listEmpty}>{t("contentTeam.detailsFailed")}</div>}
            {matches?.length === 0 && <div className={styles.listEmpty}>{t("contentTeam.noMatches")}</div>}
            {matches?.map((m, i) => {
              const home = m.team1.teamId === teamId;
              const opponent = home ? m.team2 : m.team1;
              const us = home ? m.team1score : m.team2score;
              const them = home ? m.team2score : m.team1score;
              const won = m.team1won === home;
              return (
                <Link key={`${m.matchid}-${m.mapName ?? ""}-${i}`} to={`/match/${m.matchid}`} className={`${styles.listRow} ${styles.matchRow}`}>
                  <span className={styles.matchDate}>{day(parseMatchTime(m.match_time))}</span>
                  <span className={styles.matchOpp}>
                    <img className={styles.matchOppLogo} src={opponent.teamLogo} alt="" loading="lazy" />
                    <span className={styles.matchOppName}>{opponent.teamName}</span>
                    {m.bo3 && <span className={styles.tag}>Bo3</span>}
                  </span>
                  <span className={`${styles.matchResult} ${won ? styles.win : styles.loss}`}>
                    {us}-{them}
                    <span className={styles.resultBox}>{won ? t("contentTeam.won") : t("contentTeam.lost")}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </aside>
  );
};

export default TeamPanel;
